import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { API, useAuth } from "@/context/AuthContext";
import { PLATFORMS, CONCERNS, PLATFORM_TAGS } from "@/lib/platforms";
import Tour from "@/components/site/Tour";
import { ArrowRight, ArrowUpRight, Compass as CompassIcon, Sparkles, Shuffle, Quote, Wind, SendHorizonal } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

/* ---------- hero ---------- */

function Orbit() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const R = 190;
  return (
    <div className="orbit-wrap relative w-[420px] h-[420px] max-w-full mx-auto" aria-label="The seven platforms in orbit">
      <div className="absolute inset-6 rounded-full border border-white/70" style={{ boxShadow: "inset 0 0 60px rgba(255,255,255,0.5)" }} />
      <div className="absolute inset-16 rounded-full border border-white/50" />
      {/* comet tracing the ring */}
      <div className="absolute inset-6 orbit-comet pointer-events-none">
        <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full" style={{ background: "#6B74DC", boxShadow: "0 0 14px 4px rgba(107,116,220,0.45)" }} />
      </div>
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <div className="absolute -inset-4 rounded-[2.25rem] pulse-soft pointer-events-none" style={{ background: "radial-gradient(circle, rgba(76,175,125,0.18), transparent 70%)" }} />
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="glass-ios sheen rounded-[1.75rem] px-6 py-5 w-48 text-center relative">
          <span className="w-9 h-9 blob-c mx-auto block" style={{ background: user ? user.colour : "linear-gradient(135deg, #4CAF7D, #6B74DC)" }} />
          <p className="font-display font-semibold mt-3 leading-tight">{user ? user.alias : "You, unnamed"}</p>
          <p className="text-[11px] text-[var(--ink-soft)] mt-1">One alias, seven doors, zero exposure</p>
        </motion.div>
      </div>
      <div className="absolute inset-0 orbit-ring">
        {PLATFORMS.map((p, i) => {
          const a = (360 / PLATFORMS.length) * i - 90;
          const Icon = p.icon;
          return (
            <div key={p.key} className="absolute left-1/2 top-1/2" style={{ transform: `rotate(${a}deg) translateX(${R}px) rotate(${-a}deg)`, marginLeft: "-26px", marginTop: "-26px" }}>
              <div className="orbit-upright">
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.45 + i * 0.09, type: "spring", stiffness: 240, damping: 17 }}
                  className="orbit-chip-wrap relative"
                >
                  <button
                    onClick={() => navigate(p.path)}
                    className="orbit-chip glass-strong w-[52px] h-[52px] rounded-2xl flex items-center justify-center"
                    style={{ color: p.deep, boxShadow: `0 10px 24px ${p.hue}33` }}
                    aria-label={p.name}
                    data-testid={`orbit-${p.key}`}
                  >
                    <Icon size={20} style={{ color: p.hue }} />
                  </button>
                  <span className="orbit-tip glass-strong rounded-xl px-3 py-1.5 text-xs font-semibold" style={{ color: p.deep }}>{p.name}</span>
                </motion.div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Hero() {
  const { user, setEntryOpen } = useAuth();
  return (
    <section id="hero" className="px-5 sm:px-10 pt-14 sm:pt-20 pb-6 relative overflow-hidden" style={{ background: "linear-gradient(168deg, #F3F9F4 0%, #F7FAF7 38%, #F6F4FB 78%, #F7FAF7 100%)" }}>
      <div className="pointer-events-none absolute -top-32 right-0 w-[560px] h-[560px] opacity-70" style={{ background: "radial-gradient(circle, rgba(227,245,234,0.9), transparent 65%)" }} />
      <div className="pointer-events-none absolute -bottom-40 -left-24 w-[520px] h-[520px] opacity-60" style={{ background: "radial-gradient(circle, rgba(224,218,246,0.75), transparent 66%)" }} />
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-6">
          <motion.span {...fadeUp} className="inline-flex items-center gap-2 glass-ios rounded-full px-4 py-1.5 text-xs font-semibold text-[var(--ink-soft)]">
            <span className="w-2 h-2 rounded-full bg-[#4CAF7D] pulse-soft" />
            A platform of platforms
          </motion.span>
          <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.06] mt-5 font-semibold">
            Seven doors.
            <br />
            <span className="hue-text">No waiting room.</span>
          </motion.h1>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.16 }} className="mt-5 text-base sm:text-lg text-[var(--ink-soft)] max-w-sm">
            Body, mind, food, nights, stages, people, the years ahead. Anonymous from the first click.
          </motion.p>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.24 }} className="mt-8 flex flex-wrap items-center gap-3">
            {!user && (
              <button onClick={() => setEntryOpen(true)} className="btn-ink rounded-full px-6 py-3 font-semibold" data-testid="hero-alias-btn">
                Pick your alias
              </button>
            )}
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("nc-start-tour"))}
              className="group relative inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5"
              style={{ background: "linear-gradient(135deg, #8B72EC, #5D48C0)", color: "#fff", boxShadow: "0 16px 36px -14px rgba(93,72,192,0.75)" }}
              data-testid="hero-tour-btn"
            >
              <span className="absolute inset-0 rounded-full pulse-soft" style={{ boxShadow: "0 0 0 6px rgba(139,114,236,0.18)" }} />
              <Sparkles size={15} className="relative" />
              <span className="relative">Show me around</span>
            </button>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("nc-open-compass"))}
              className="btn-line rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2"
              data-testid="hero-compass-btn"
            >
              <CompassIcon size={16} style={{ color: "#5D48C0" }} />
              Find where to start
            </button>
          </motion.div>
          {/* mobile platform chips */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }} className="sm:hidden mt-8 flex gap-2 overflow-x-auto pb-2 -mx-5 px-5">
            {PLATFORMS.map((p) => {
              const Icon = p.icon;
              return (
                <Link key={p.key} to={p.path} className="glass-strong rounded-2xl px-4 py-3 flex items-center gap-2 shrink-0" data-testid={`hero-chip-${p.key}`}>
                  <Icon size={15} style={{ color: p.hue }} />
                  <span className="text-xs font-semibold whitespace-nowrap">{p.name}</span>
                </Link>
              );
            })}
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }} className="lg:col-span-6 hidden sm:block">
          <Orbit />
        </motion.div>
      </div>

      <motion.div {...fadeUp} className="max-w-6xl mx-auto mt-12">
        <p className="kicker text-[var(--ink-soft)] mb-4">Start from what you feel</p>
        <div className="marquee" data-testid="concern-pills">
          <div className="marquee-track">
            {[0, 1].map((copy) => (
              <div key={copy} className="flex gap-3" aria-hidden={copy === 1}>
                {CONCERNS.map((c) => (
                  <Link
                    key={`${copy}-${c.key}`}
                    to={c.to}
                    tabIndex={copy === 1 ? -1 : 0}
                    className="concern-pill rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2.5 whitespace-nowrap"
                    data-testid={copy === 0 ? `concern-${c.key}` : undefined}
                  >
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: PLATFORM_TAGS[c.key].hue }} />
                    {c.label}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
}

/* ---------- spectrum ---------- */

function Spectrum() {
  const [active, setActive] = useState("mental");
  const canHover = () => window.matchMedia("(hover: hover) and (min-width: 1024px)").matches;
  return (
    <section id="platforms" className="px-5 sm:px-10 py-16 relative">
      <div className="pointer-events-none absolute -left-32 top-1/3 w-[420px] h-[420px] opacity-50" style={{ background: "radial-gradient(circle, rgba(255,233,220,0.9), transparent 65%)" }} />
      <div className="max-w-6xl mx-auto relative">
        <motion.div {...fadeUp} className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="kicker text-[var(--ink-soft)]">The seven</p>
            <h2 className="font-display text-base md:text-lg font-semibold mt-2 text-[var(--ink-soft)]">Open a colour. Each one is a complete world.</h2>
          </div>
        </motion.div>
        <motion.div {...fadeUp} className="spectrum flex-col lg:flex-row mt-8 lg:h-[460px]">
          {PLATFORMS.map((p) => {
            const Icon = p.icon;
            const isActive = active === p.key;
            return (
              <div
                key={p.key}
                onClick={() => setActive(p.key)}
                onMouseEnter={() => canHover() && setActive(p.key)}
                className={`spectrum-col grain ${isActive ? "active" : ""}`}
                style={{
                  flexGrow: isActive ? 6 : 1,
                  flexBasis: 0,
                  background: `linear-gradient(165deg, ${p.hue}, ${p.deep})`,
                }}
                data-testid={`seven-tile-${p.key}`}
                role="button"
                aria-expanded={isActive}
              >
                <div className="col-mini absolute inset-0 flex lg:flex-col items-center lg:items-center justify-start lg:justify-between px-5 lg:px-0 lg:py-6 gap-3 text-white">
                  <span className="glass-dark w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
                    <Icon size={17} />
                  </span>
                  <span className="font-display font-semibold text-sm whitespace-nowrap lg:[writing-mode:vertical-rl] lg:rotate-180">{p.name}</span>
                  <span className="hidden lg:block w-1.5 h-1.5 rounded-full bg-white/70" />
                </div>
                <div className="col-detail absolute inset-0 p-7 sm:p-9 flex flex-col justify-between text-white">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="glass-dark w-11 h-11 rounded-2xl flex items-center justify-center">
                        <Icon size={19} />
                      </span>
                      <span className="kicker text-white/70">{p.tagline}</span>
                    </div>
                    <h3 className="font-display text-3xl sm:text-4xl font-semibold mt-5 leading-tight">{p.name}</h3>
                    <p className="mt-3 text-white/85 text-sm sm:text-base max-w-md">{p.intro}</p>
                    <div className="mt-5 flex flex-wrap gap-2 max-w-lg">
                      {p.features.slice(0, 4).map((f) => {
                        const FIcon = f.icon;
                        return (
                          <span key={f.name} className="glass-dark inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold">
                            <FIcon size={12} />
                            {f.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mt-6">
                    <Link
                      to={p.path}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                      style={{ color: p.deep }}
                      data-testid="showcase-enter-btn"
                    >
                      Step inside <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* ---------- soul band ---------- */

const SOUL_WORDS = ["unhurried", "unseen", "unpriced", "unjudged", "yours"];

function SoulBand() {
  return (
    <section id="soul" className="py-14 relative overflow-hidden" data-testid="soul-band">
      <div className="relative -rotate-1 scale-[1.02] border-y border-white/80 py-8" style={{ background: "linear-gradient(90deg, rgba(230,245,236,0.75), rgba(234,236,251,0.75), rgba(251,234,230,0.75))", backdropFilter: "blur(8px)" }}>
        <div className="marquee">
          <div className="marquee-track soul items-baseline">
            {[0, 1].map((copy) => (
              <div key={copy} className="flex items-baseline gap-10 pr-10" aria-hidden={copy === 1}>
                {SOUL_WORDS.map((w, i) => {
                  const hue = PLATFORMS[(i * 2) % 7].hue;
                  const deep = PLATFORMS[(i * 2) % 7].deep;
                  return (
                    <span key={`${copy}-${w}`} className="flex items-baseline gap-10">
                      <span
                        className={`font-display text-6xl sm:text-7xl font-semibold whitespace-nowrap leading-none ${i % 2 === 0 ? "soul-fill" : "soul-outline"}`}
                        style={i % 2 === 0 ? { backgroundImage: `linear-gradient(120deg, ${hue}, ${deep})` } : {}}
                      >
                        {w}
                      </span>
                      <span className="w-2.5 h-2.5 rounded-full shrink-0 self-center pulse-soft" style={{ background: hue }} />
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- one ordinary day ---------- */

const DAY = [
  { time: "07:10", key: "sleep", text: "Light first, coffee a little later. Your body clock says so." },
  { time: "13:00", key: "vitality", text: "A plate built from what is actually in the fridge." },
  { time: "17:30", key: "vitality", text: "Forty minutes, your plan, your equipment. Logged as you go." },
  { time: "19:00", key: "relationships", text: "The talk you kept postponing, opened with a script that lands." },
  { time: "21:15", key: "mental", text: "Ten minutes in the studio. Whatever you make stays yours." },
  { time: "22:40", key: "sleep", text: "The runway begins, built back from tomorrow's 07:00." },
];

function DayArc() {
  return (
    <section id="day" className="px-5 sm:px-10 py-16 relative overflow-hidden">
      <div className="pointer-events-none absolute top-20 -left-40 w-[500px] h-[500px] opacity-60" style={{ background: "radial-gradient(circle, rgba(239,234,252,0.9), transparent 65%)" }} />
      <div className="max-w-6xl mx-auto relative">
        <motion.div {...fadeUp} className="max-w-md">
          <p className="kicker text-[var(--ink-soft)]">One ordinary day</p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-3 leading-tight">A day, held together</h2>
          <p className="text-[var(--ink-soft)] mt-3 text-sm sm:text-base">Not seven apps. One thread running through the hours you already live.</p>
        </motion.div>
        <div className="mt-10 space-y-4">
          {DAY.map((m, i) => {
            const p = PLATFORM_TAGS[m.key];
            const platform = PLATFORMS.find((x) => x.key === m.key);
            const Icon = platform.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                style={{ marginLeft: `min(${i * 7}%, 42%)` }}
                className="max-w-md"
              >
                <Link to={platform.path} className="glass-ios rounded-3xl px-5 py-4 flex items-center gap-4 hover:-translate-y-1 transition-transform duration-300 block" data-testid={`day-moment-${i}`}>
                  <span className="font-display font-semibold text-sm tabular-nums rounded-xl px-2.5 py-1.5" style={{ background: `${p.hue}16`, color: p.hue }}>{m.time}</span>
                  <p className="text-sm flex-1">{m.text}</p>
                  <span className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${p.hue}16`, color: p.hue }}>
                    <Icon size={14} />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- breath ---------- */

const PHASES = [
  { label: "Breathe in", secs: 4 },
  { label: "Hold it there", secs: 4 },
  { label: "Let it go", secs: 6 },
];

function Breath() {
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    if (!running) return;
    let idx = 0;
    setPhase(0);
    let timer;
    const next = () => {
      idx = (idx + 1) % PHASES.length;
      setPhase(idx);
      timer = setTimeout(next, PHASES[idx].secs * 1000);
    };
    timer = setTimeout(next, PHASES[0].secs * 1000);
    return () => clearTimeout(timer);
  }, [running]);
  const p = PHASES[phase];
  const secs = p.secs;
  return (
    <section id="breath" className="px-5 sm:px-10 py-16">
      <div className="max-w-6xl mx-auto">
        <motion.div {...fadeUp} className="rounded-[2.75rem] p-8 sm:p-12 relative overflow-hidden"
          style={{ background: "linear-gradient(140deg, #16342A 0%, #1E4A3A 46%, #2A3F63 100%)", boxShadow: "0 60px 120px -70px rgba(20,50,40,0.9)" }}>
          {/* the light behind it */}
          <motion.span className="pointer-events-none absolute rounded-full" style={{ right: "-8%", top: "-30%", width: 520, height: 520, background: "radial-gradient(circle, rgba(129,222,175,0.34), transparent 66%)", filter: "blur(20px)" }}
            animate={running ? { scale: [1, 1.14, 1], opacity: [0.7, 1, 0.7] } : { scale: 1, opacity: 0.6 }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }} />
          <span className="pointer-events-none absolute rounded-full" style={{ left: "-14%", bottom: "-34%", width: 460, height: 460, background: "radial-gradient(circle, rgba(122,140,232,0.3), transparent 68%)", filter: "blur(24px)" }} />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <p className="text-[10px] tracking-[0.24em] uppercase text-white/45">A small pause</p>
              <h2 className="font-display text-[32px] sm:text-[44px] font-semibold mt-3 leading-[1.04] text-white">
                Nothing here<br />rushes you
              </h2>
              <p className="text-white/60 mt-4 text-sm sm:text-base max-w-sm">
                Four in, four held, six out. One breath, and then the page waits again.
              </p>
              <button
                onClick={() => setRunning(!running)}
                className="mt-8 rounded-full px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2.5 transition-transform hover:-translate-y-0.5"
                style={running ? { background: "rgba(255,255,255,0.12)", color: "#fff", border: "1px solid rgba(255,255,255,0.28)" } : { background: "#8DE3B4", color: "#123024", boxShadow: "0 20px 44px -18px rgba(141,227,180,0.7)" }}
                data-testid="breath-toggle"
              >
                <Wind size={15} />
                {running ? "That was enough" : "One breath"}
              </button>
              <div className="flex gap-2 mt-8">
                {PHASES.map((x, i) => (
                  <div key={x.label} className="flex-1">
                    <div className="h-[3px] rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.16)" }}>
                      <motion.span className="block h-full" style={{ background: running && i === phase ? "#8DE3B4" : "rgba(255,255,255,0.4)" }}
                        initial={false} animate={{ width: running && i === phase ? "100%" : running && i < phase ? "100%" : "0%" }}
                        transition={{ duration: running && i === phase ? x.secs : 0.3, ease: "linear" }} />
                    </div>
                    <p className="text-[10px] mt-2 tracking-[0.12em] uppercase" style={{ color: running && i === phase ? "#8DE3B4" : "rgba(255,255,255,0.35)" }}>{x.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 flex items-center justify-center">
              <div className="relative w-[260px] h-[260px] sm:w-[320px] sm:h-[320px] flex items-center justify-center">
                {[0, 1, 2, 3].map((k) => (
                  <motion.span key={k} className="absolute rounded-full" style={{ inset: k * 22, border: `1px solid rgba(141,227,180,${0.34 - k * 0.06})` }}
                    animate={running ? { scale: [1, 1.07, 1], opacity: [0.5, 1, 0.5] } : { scale: 1, opacity: 0.45 }}
                    transition={{ duration: secs * 2, repeat: Infinity, ease: "easeInOut", delay: k * 0.4 }} />
                ))}
                <motion.span className="absolute rounded-full" style={{ inset: 78, background: "radial-gradient(circle at 38% 32%, rgba(255,255,255,0.9), #8DE3B4 42%, #4F9FD8 100%)", boxShadow: "0 24px 70px -18px rgba(141,227,180,0.6)" }}
                  animate={running ? { scale: p.label === "Breathe in" ? [0.72, 1] : p.label === "Hold" ? 1 : [1, 0.72] } : { scale: 0.82 }}
                  transition={{ duration: secs, ease: "easeInOut" }} />
                <div className="relative text-center">
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={running ? p.label : "still"}
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                      className="font-display font-semibold text-lg sm:text-xl" style={{ color: "#0E2A20" }}
                      data-testid="breath-phase"
                    >
                      {running ? p.label : "Ready when you are"}
                    </motion.p>
                  </AnimatePresence>
                  {running && <p className="text-[11px] mt-1 tabular-nums" style={{ color: "rgba(14,42,32,0.55)" }}>{secs} seconds</p>}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------- anonymity ---------- */

const ALIAS_IDEAS = ["Sova", "Reka", "Bor", "Jasen", "Zora", "Sever", "Kap", "Vuk", "Iva", "Talas", "Breza", "Oblak"];

function AliasSection() {
  const { user, setEntryOpen } = useAuth();
  const [idx, setIdx] = useState(0);
  const [hueIdx, setHueIdx] = useState(1);
  const alias = ALIAS_IDEAS[idx];
  const hue = PLATFORMS[hueIdx].hue;
  const shuffle = () => {
    setIdx((i) => (i + 1 + Math.floor(Math.random() * (ALIAS_IDEAS.length - 2))) % ALIAS_IDEAS.length);
    setHueIdx((h) => (h + 1 + Math.floor(Math.random() * 5)) % PLATFORMS.length);
  };
  return (
    <section id="alias" className="px-5 sm:px-10 py-16">
      <div className="max-w-6xl mx-auto">
        <motion.div {...fadeUp} className="relative overflow-hidden rounded-[2.75rem]" style={{ background: "linear-gradient(135deg, #241B3E 0%, #3A2A63 48%, #4A3170 100%)", boxShadow: "0 60px 120px -70px rgba(36,27,62,0.9)" }}>
          <div className="pointer-events-none absolute -left-24 -bottom-32 w-[420px] h-[420px] opacity-40" style={{ background: "radial-gradient(circle, #9C8AE8, transparent 65%)" }} />
          <div className="pointer-events-none absolute right-0 -top-32 w-[420px] h-[420px] opacity-30" style={{ background: "radial-gradient(circle, #F0C98A, transparent 66%)" }} />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 p-8 sm:p-14 items-center">
            <div className="text-white md:col-span-7">
              <p className="text-[10px] tracking-[0.24em] uppercase text-white/50">Anonymous by design</p>
              <h2 className="font-display text-[32px] sm:text-[44px] font-semibold mt-3 leading-[1.04]">
                Cared for,<br />without being seen
              </h2>
              <p className="mt-5 text-white/72 max-w-md text-sm sm:text-base">
                Where we come from, asking about your own health has witnesses. Here you are a name you invented and a colour you chose, and even the doctors answering never learn who you are.
              </p>
              <div className="flex flex-wrap gap-x-8 gap-y-3 mt-8">
                {[["0", "real names collected"], ["1", "colour, chosen by you"], ["7", "doors, one alias"]].map(([a, b]) => (
                  <div key={b}>
                    <p className="font-display text-2xl font-semibold leading-none" style={{ color: "#F0C98A" }}>{a}</p>
                    <p className="text-[11px] mt-1 text-white/50">{b}</p>
                  </div>
                ))}
              </div>
              {!user && (
                <button onClick={() => setEntryOpen(true)} className="mt-8 rounded-full px-7 py-3.5 text-sm font-semibold hover:-translate-y-0.5 transition-transform" style={{ background: "#F0C98A", color: "#2A1F47", boxShadow: "0 20px 44px -18px rgba(240,201,138,0.6)" }} data-testid="alias-section-btn">
                  Pick your alias
                </button>
              )}
            </div>
            <div className="md:col-span-5 flex justify-center md:justify-end">
              <div className="rounded-[2rem] p-8 w-full max-w-xs text-white relative overflow-hidden" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.18)", backdropFilter: "blur(18px)" }} data-testid="alias-preview-card">
                <p className="text-[10px] tracking-[0.22em] uppercase text-white/50">You could appear as</p>
                <AnimatePresence mode="wait">
                  <motion.div key={alias + hue} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }} className="flex items-center gap-4 mt-5">
                    <span className="w-14 h-14 blob-c shrink-0" style={{ background: hue, boxShadow: `0 8px 24px ${hue}66` }} />
                    <div>
                      <p className="font-display text-2xl font-semibold">{alias}</p>
                      <p className="text-xs text-white/50">Member since today</p>
                    </div>
                  </motion.div>
                </AnimatePresence>
                <button onClick={shuffle} className="w-full rounded-full py-2.5 mt-7 text-xs font-semibold inline-flex items-center justify-center gap-2 bg-white/10 border border-white/25 hover:bg-white/20 transition-colors" data-testid="alias-shuffle-btn">
                  <Shuffle size={13} /> Try another
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------- voices ---------- */

function Voices() {
  const [specialists, setSpecialists] = useState([]);
  useEffect(() => {
    axios.get(`${API}/specialists`).then((r) => setSpecialists(r.data)).catch((e) => console.warn("specialists load failed", e));
  }, []);
  if (specialists.length === 0) return null;
  return (
    <section id="voices" className="py-16 relative" style={{ background: "linear-gradient(180deg, rgba(246,244,251,0) 0%, rgba(243,240,250,0.85) 22%, rgba(243,240,250,0.85) 74%, rgba(247,250,247,1) 100%)" }}>
      <motion.div {...fadeUp} className="max-w-6xl mx-auto px-5 sm:px-10 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="kicker text-[var(--ink-soft)]">The people answering</p>
          <h2 className="font-display text-base md:text-lg font-semibold mt-2 text-[var(--ink-soft)]">Real names, real credentials. Yours stays invented.</h2>
        </div>
      </motion.div>
      <motion.div {...fadeUp} className="marquee mt-8" data-testid="voices-marquee">
        <div className="marquee-track slow">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex gap-3" aria-hidden={copy === 1}>
              {specialists.map((s) => {
                const tag = PLATFORM_TAGS[s.platform] || PLATFORM_TAGS.general;
                return (
                  <div key={`${copy}-${s.slug}`} className="glass-ios rounded-3xl p-6 w-80 shrink-0" data-testid={copy === 0 ? `voice-${s.slug}` : undefined}>
                    <Quote size={16} style={{ color: tag.hue }} />
                    <p className="font-display text-base font-medium leading-snug mt-3 min-h-[72px]">{s.philosophy}</p>
                    <div className="flex items-center gap-3 mt-4">
                      <span className="w-9 h-9 blob-c flex items-center justify-center text-white text-xs font-semibold" style={{ background: tag.hue }}>
                        {s.name.split(" ").map((w) => w[0]).slice(-2).join("")}
                      </span>
                      <div>
                        <p className="text-sm font-semibold">{s.name}</p>
                        <p className="text-xs text-[var(--ink-soft)]">{s.role} · {tag.name}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}

/* ---------- the shape of it ---------- */

function Count({ to }) {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      const t0 = performance.now();
      const dur = 1400;
      const step = (t) => {
        const p = Math.min(1, (t - t0) / dur);
        setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
      io.disconnect();
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [to]);
  return <span ref={ref}>{v}</span>;
}

const SHAPE = [
  { n: 7, hue: "#4CAF7D", label: "doors, one address", sub: "Each a complete world of its own" },
  { n: 46, hue: "#8B72EC", label: "rooms and instruments", sub: "Built, filmed and written by the people answering" },
  { n: 14, hue: "#E26D5A", label: "specialists, real names", sub: "Doctors, therapists and coaches on the record" },
  { n: 0, hue: "#4C9EDA", label: "times your name is asked", sub: "And zero times anything costs anything" },
];

function Shape() {
  return (
    <section id="shape" className="px-5 sm:px-10 py-16">
      <div className="max-w-6xl mx-auto">
        <motion.p {...fadeUp} className="kicker text-[var(--ink-soft)]">The shape of it</motion.p>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {SHAPE.map((s, i) => (
            <motion.div
              key={s.label}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.1 }}
              className={`glass-ios tilt rounded-[2rem] p-7 ${i % 2 === 1 ? "lg:mt-8" : ""}`}
              data-testid={`shape-stat-${i}`}
            >
              <p className="font-display text-5xl sm:text-6xl font-semibold tabular-nums" style={{ color: s.hue }}>
                <Count to={s.n} />
              </p>
              <p className="font-display font-semibold mt-2">{s.label}</p>
              <p className="text-xs text-[var(--ink-soft)] mt-1.5">{s.sub}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- journal ---------- */

function Journal() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/blog`).then((r) => setPosts(r.data.slice(0, 3))).catch((e) => console.warn("journal load failed", e));
  }, []);
  if (posts.length === 0) return null;
  const [lead, ...rest] = posts;
  const leadTag = PLATFORM_TAGS[lead.platform] || PLATFORM_TAGS.general;
  return (
    <section id="journal" className="px-5 sm:px-10 py-16">
      <div className="max-w-6xl mx-auto">
        <motion.div {...fadeUp} className="flex items-end justify-between">
          <div>
            <p className="kicker text-[var(--ink-soft)]">Fresh from the blog</p>
            <h2 className="font-display text-base md:text-lg font-semibold mt-2 text-[var(--ink-soft)]">Short reads, no jargon.</h2>
          </div>
          <Link to="/blog" className="text-sm font-semibold hover:opacity-60 transition-opacity" data-testid="home-blog-link">All posts</Link>
        </motion.div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-5">
          <motion.div {...fadeUp} className="md:col-span-7">
            <Link to={`/blog/${lead.slug}`} className="relative overflow-hidden rounded-[2.5rem] p-8 sm:p-10 flex flex-col justify-between min-h-[320px] block hover:-translate-y-1 transition-transform duration-300 grain" style={{ background: `linear-gradient(150deg, ${leadTag.hue}, ${leadTag.hue}CC)` }} data-testid={`home-post-${lead.slug}`}>
              <span className="glass-dark self-start inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3.5 py-1.5 text-white">
                {leadTag.name}
              </span>
              <div className="text-white mt-10">
                <h3 className="font-display text-2xl sm:text-3xl font-semibold leading-snug max-w-md">{lead.title}</h3>
                <p className="text-sm text-white/80 mt-3 max-w-md">{lead.excerpt}</p>
                <p className="text-xs text-white/60 mt-5">{lead.author} · {lead.read_minutes} min</p>
              </div>
            </Link>
          </motion.div>
          <div className="md:col-span-5 flex flex-col gap-5">
            {rest.map((post, i) => {
              const tag = PLATFORM_TAGS[post.platform] || PLATFORM_TAGS.general;
              return (
                <motion.div key={post.slug} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 + i * 0.1 }} className="flex-1">
                  <Link to={`/blog/${post.slug}`} className="glass-ios tilt rounded-[2rem] p-7 flex flex-col justify-between h-full" data-testid={`home-post-${post.slug}`}>
                    <span className="inline-flex self-start items-center gap-2 text-xs font-semibold rounded-full px-3 py-1" style={{ background: `${tag.hue}16`, color: tag.hue }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: tag.hue }} />
                      {tag.name}
                    </span>
                    <h3 className="font-display text-lg font-semibold mt-3 leading-snug">{post.title}</h3>
                    <p className="text-xs text-[var(--ink-soft)] mt-3">{post.author} · {post.read_minutes} min</p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- ask bar ---------- */

const ASK_HINTS = [
  "I keep waking at three in the morning...",
  "My knee clicks when I run, is that bad...",
  "I want to cut back on drinking but not fully stop...",
  "My mother forgets words lately...",
  "How often should I check my blood pressure at 34...",
];

function AskBar() {
  const [text, setText] = useState("");
  const [hint, setHint] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setHint((h) => (h + 1) % ASK_HINTS.length), 3200);
    return () => clearInterval(t);
  }, []);
  const send = () => {
    window.dispatchEvent(new CustomEvent("nc-open-assistant", { detail: { prefill: text.trim() } }));
    setText("");
  };
  return (
    <section id="ask" className="px-5 sm:px-10 py-10">
      <div className="max-w-3xl mx-auto">
        <motion.div {...fadeUp} className="glass-ios sheen rounded-[2rem] p-3 pl-6 flex items-center gap-3" data-testid="ask-bar">
          <Sparkles size={18} style={{ color: "#8B72EC" }} className="shrink-0" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={ASK_HINTS[hint]}
            className="flex-1 bg-transparent outline-none text-sm sm:text-base py-3 placeholder:text-[var(--ink-soft)]"
            data-testid="ask-bar-input"
          />
          <button onClick={send} className="btn-ink rounded-[1.4rem] px-5 py-3 text-sm font-semibold inline-flex items-center gap-2 shrink-0" data-testid="ask-bar-send">
            <SendHorizonal size={15} />
            <span className="hidden sm:inline">Ask Novcovek</span>
          </button>
        </motion.div>
        <motion.p {...fadeUp} className="text-xs text-[var(--ink-soft)] text-center mt-3">
          Say it in your own words. Five messages per visit, no name required.
        </motion.p>
      </div>
    </section>
  );
}

/* ---------- closing ---------- */

function Closing() {
  const { user, setEntryOpen } = useAuth();
  return (
    <section id="closing" className="px-5 sm:px-10 pt-10 pb-24">
      <div className="max-w-3xl mx-auto text-center">
        <motion.p {...fadeUp} className="font-display text-3xl sm:text-4xl font-medium leading-snug">
          Health is not arranged the way hospitals are. It is arranged the way days are.
        </motion.p>
        <motion.div {...fadeUp} className="mt-4 flex justify-center gap-1.5">
          {PLATFORMS.map((p) => (
            <Link key={p.key} to={p.path} aria-label={p.name} className="h-1.5 w-8 rounded-full hover:scale-y-[2] transition-transform" style={{ background: p.hue }} />
          ))}
        </motion.div>
        <motion.div {...fadeUp} className="mt-8 flex flex-wrap justify-center gap-3">
          {!user && (
            <button onClick={() => setEntryOpen(true)} className="btn-ink rounded-full px-7 py-3 font-semibold" data-testid="closing-alias-btn">
              Pick your alias
            </button>
          )}
          <button onClick={() => window.dispatchEvent(new CustomEvent("nc-open-assistant"))} className="btn-line rounded-full px-7 py-3 font-semibold" data-testid="find-ask-btn">
            Ask Novcovek
          </button>
          <Link to="/why" className="inline-flex items-center gap-1.5 px-2 py-3 text-sm font-semibold text-[var(--ink-soft)] hover:text-[var(--ink)] transition-colors" data-testid="home-why-link">
            Why this exists <ArrowUpRight size={15} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main className="pt-16" data-testid="home-page">
      <Hero />
      <Spectrum />
      <SoulBand />
      <DayArc />
      <Breath />
      <AliasSection />
      <Voices />
      <Shape />
      <Journal />
      <AskBar />
      <Closing />
      <Tour />
    </main>
  );
}
